/**
 * Dash Geral — só admin. O retrato da operação de todos os gestores numa tela.
 *
 * Desenho aprovado no protótipo "Geral Meta Rocket" (09/10/2026). Receita e
 * base vêm da tela de Leads (receita pela utm_campaign, só páginas ativas);
 * envio e rankings vêm do Analytics do bot (receita de funil e disparo pela
 * utm_content). Cada bloco carrega sozinho: se um falhar, só ele avisa.
 *
 * O filtro de usuário é o global (FiltroUsuarioAdmin): clicar num gestor na
 * tabela escolhe ele para esta tela e para todas as outras.
 */
import { useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Loader2, RefreshCw } from 'lucide-react';
import FiltroUsuarioAdmin, { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';
import {
    falhou, getDashGeral, type CanalGeral, type DashGeral, type GestorGeral, type PeriodoGeral,
} from '@/services/api/admin-geral';

const COR = {
    gold: '#b57209', goldBg: '#fdf6e9', goldLine: '#efdfc0',
    flow: '#0e7490', flowBg: '#edf7fa', broad: '#12805c', broadBg: '#eef7f2',
    ink: '#14161a', muted: '#5a6270', faint: '#8c93a0', line: '#e3e6eb', grid: '#eef0f3',
    red: '#dc2626', amber: '#b45309', blue: '#2563eb', ok: '#12805c', soft: '#cbd2db',
};

const PERIODOS: { k: PeriodoGeral; l: string }[] = [
    { k: 'hoje', l: 'Hoje' }, { k: 'ontem', l: 'Ontem' }, { k: '7d', l: '7 dias' },
    { k: '30d', l: '30 dias' }, { k: 'mes', l: 'Este mês' },
];

// ── formatação (dinheiro em centavos; % já em 0-100) ───────────────────────
const NF = new Intl.NumberFormat('pt-BR');
const NF2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NFC = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });
const TRACO = '—';
const ok = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const int = (v?: number | null) => (ok(v) ? NF.format(v) : TRACO);
const usd = (c?: number | null) => (ok(c) ? `US$ ${NF2.format(c / 100)}` : TRACO);
const usdK = (c?: number | null) => (ok(c) ? `US$ ${NFC.format(c / 100)}` : TRACO);
const pct = (v?: number | null, d = 1) => (ok(v) ? `${v.toFixed(d).replace('.', ',')}%` : TRACO);
const delta = (v?: number | null) => (ok(v) ? `${v >= 0 ? '▲' : '▼'} ${Math.abs(v).toFixed(1).replace('.', ',')}%` : TRACO);
const assinado = (v?: number | null) => (ok(v) ? `${v > 0 ? '+' : v < 0 ? '−' : ''}${NF.format(Math.abs(v))}` : TRACO);
const regiao = (() => {
    try {
        const dn = new Intl.DisplayNames(['pt-BR'], { type: 'region' });
        return (c: string) => { try { return dn.of(c) ?? c; } catch { return c; } };
    } catch {
        return (c: string) => c;
    }
})();
const nomeGestor = (g: GestorGeral) => g.owner_name || `Usuário ${g.user_id}`;

// ── peças ──────────────────────────────────────────────────────────────────
function Rotulo({ children }: { children: ReactNode }) {
    return <span className="font-mono text-[10px] font-semibold uppercase tracking-[1.3px] text-[#8c93a0]">{children}</span>;
}

function Kpi({ rotulo, valor, cor, destaque, children }: {
    rotulo: string; valor: string; cor?: string; destaque?: boolean; children?: ReactNode;
}) {
    return (
        <div
            className="flex flex-col gap-1.5 rounded-md border px-4 pb-3 pt-3.5"
            style={{
                background: destaque ? COR.goldBg : '#fff',
                borderColor: destaque ? COR.goldLine : COR.line,
                boxShadow: `inset 3px 0 0 ${cor ?? COR.line}`,
            }}
        >
            <Rotulo>{rotulo}</Rotulo>
            <span className="text-[27px] font-semibold leading-none tracking-[-0.8px] tabular-nums" style={{ color: cor ?? COR.ink }}>
                {valor}
            </span>
            <span className="text-[11.5px] text-[#5a6270]">{children}</span>
        </div>
    );
}

function Cartao({ titulo, sub, direita, children, id }: {
    titulo: string; sub?: string; direita?: ReactNode; children: ReactNode; id?: string;
}) {
    return (
        <div id={id} className="flex min-w-0 flex-col rounded-md border border-[#e3e6eb] bg-white">
            <div className="flex flex-wrap items-start justify-between gap-2.5 px-3.5 pb-2 pt-3">
                <div>
                    <p className="m-0 text-[13px] font-semibold text-[#14161a]">{titulo}</p>
                    {sub && <p className="m-0 mt-0.5 text-[11.5px] italic text-[#5a6270]">{sub}</p>}
                </div>
                {direita}
            </div>
            <div className="flex min-w-0 flex-col gap-2 px-3.5 pb-3.5 pt-1">{children}</div>
        </div>
    );
}

function Secao({ titulo, sub }: { titulo: string; sub?: string }) {
    return (
        <div className="mt-2.5 flex items-baseline gap-2.5">
            <h2 className="m-0 text-[15px] font-bold tracking-[-0.2px]">{titulo}</h2>
            {sub && <span className="text-xs text-[#5a6270]">{sub}</span>}
        </div>
    );
}

function Legenda({ itens }: { itens: [string, string][] }) {
    return (
        <div className="flex flex-wrap gap-3 text-[11.5px] text-[#5a6270]">
            {itens.map(([cor, nome]) => (
                <span key={nome}><i className="mr-1.5 inline-block h-[9px] w-[9px] rounded-sm align-[-1px]" style={{ background: cor }} />{nome}</span>
            ))}
        </div>
    );
}

function Indisponivel({ motivo }: { motivo?: string }) {
    return (
        <div className="flex min-h-[120px] items-center justify-center gap-1.5 text-xs text-[#5a6270]" title={motivo}>
            <i className="inline-block h-1.5 w-1.5 rounded-full bg-[#dc2626]" /> Não carregou
        </div>
    );
}

function Tag({ children, tom = 'info' }: { children: ReactNode; tom?: 'crit' | 'warn' | 'info' | 'ok' | 'dark' | 'flow' }) {
    const t = {
        crit: 'bg-[#fef2f2] text-[#dc2626] border-[#fecaca]',
        warn: 'bg-[#fffbeb] text-[#b45309] border-[#fcd34d]',
        info: 'bg-[#f0f2f5] text-[#5a6270] border-[#e3e6eb]',
        ok: 'bg-[#ecfdf5] text-[#12805c] border-[#a7f3d0]',
        dark: 'bg-[#14161a] text-white border-transparent',
        flow: 'bg-[#edf7fa] text-[#0e7490] border-[#bae6fd]',
    }[tom];
    return <span className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 font-mono text-[10.5px] font-semibold ${t}`}>{children}</span>;
}

const th = 'sticky top-0 z-[1] whitespace-nowrap border-b border-[#e3e6eb] bg-white px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[1.1px] text-[#8c93a0]';
const td = 'border-b border-[#eef0f3] px-3 py-2 align-top text-[#5a6270]';
const tdN = `${td} whitespace-nowrap text-right tabular-nums text-[#14161a]`;

function Dica({ active, label, payload, fmt }: {
    active?: boolean; label?: string | number; fmt: (v: number) => string;
    payload?: { name?: string; value?: number; color?: string; fill?: string }[];
}) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-md border border-[#e3e6eb] bg-white px-2.5 py-1.5 text-[11.5px] shadow-md">
            <div className="mb-0.5 font-mono text-[10px] uppercase tracking-wider text-[#8c93a0]">{label}</div>
            {payload.map((p) => (
                <div key={p.name}><span style={{ color: p.color ?? p.fill }}>{p.name}</span>: {fmt(Number(p.value))}</div>
            ))}
        </div>
    );
}

// ── tela ───────────────────────────────────────────────────────────────────
export default function Geral() {
    const { isAdmin, usuarioId, setUsuarioId } = useFiltroUsuarioAdmin();
    const [periodo, setPeriodo] = useState<PeriodoGeral>('7d');

    const q = useQuery({
        queryKey: ['dash-geral', periodo, usuarioId ?? null],
        queryFn: () => getDashGeral(periodo, usuarioId ?? undefined),
        enabled: isAdmin,
        staleTime: 1000 * 60 * 2,
        refetchOnWindowFocus: false,
    });

    if (!isAdmin) return <Navigate to="/meta/analytics-bot" replace />;

    const d = q.data;
    const rotuloPeriodo = PERIODOS.find((p) => p.k === periodo)?.l ?? periodo;

    return (
        <div className="flex min-h-full flex-col gap-3.5 rounded-xl bg-[#f4f6f8] px-[clamp(16px,3vw,28px)] pb-8 pt-6 text-sm text-[#14161a] tabular-nums">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="m-0 text-xl font-bold tracking-[-0.4px]">Geral</h1>
                    <p className="m-0 mt-0.5 text-[12.5px] text-[#5a6270]">
                        {rotuloPeriodo} · só páginas ativas{d ? ` · ${d.periodo.inicio.split('-').reverse().join('/')} a ${d.periodo.fim.split('-').reverse().join('/')}` : ''}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => q.refetch()}
                    className="flex items-center gap-1.5 text-[12.5px] text-[#5a6270] hover:text-[#14161a]"
                    disabled={q.isFetching}
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${q.isFetching ? 'animate-spin' : ''}`} />
                    {d ? `atualizado às ${new Date(d.gerado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : 'carregando'}
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-md border border-[#e3e6eb] bg-white px-3 py-2.5">
                <Rotulo>Período</Rotulo>
                <div className="inline-flex overflow-hidden rounded-md border border-[#e3e6eb]">
                    {PERIODOS.map((p) => (
                        <button
                            key={p.k}
                            type="button"
                            onClick={() => setPeriodo(p.k)}
                            aria-pressed={periodo === p.k}
                            className={`border-r border-[#e3e6eb] px-3 py-1.5 text-xs font-medium last:border-r-0 ${periodo === p.k ? 'bg-[#14161a] text-white' : 'text-[#5a6270] hover:bg-[#f8f9fb]'}`}
                        >
                            {p.l}
                        </button>
                    ))}
                </div>
                <Rotulo>Usuário</Rotulo>
                <FiltroUsuarioAdmin className="w-56" />
            </div>

            <div className="rounded-md border border-[#fcd34d] bg-[#fffbeb] px-3 py-2 text-[12.5px] text-[#78350f]">
                Receita e leads vêm da tela de <b>Leads</b> (receita pela utm_campaign de cada página, só páginas ativas).
                Envio e rankings vêm do <b>Analytics</b> (receita de funil e disparo pela utm_content, que cobre menos).
            </div>

            {q.isLoading && (
                <div className="flex items-center gap-2 rounded-md border border-[#e3e6eb] bg-white p-6 text-[#5a6270]">
                    <Loader2 className="h-4 w-4 animate-spin" /> Montando o retrato da operação… o período com hoje leva alguns segundos.
                </div>
            )}
            {q.isError && (
                <div className="rounded-md border border-[#fecaca] bg-[#fef2f2] p-4 text-[#dc2626]">Não foi possível carregar a Dash Geral.</div>
            )}

            {d && <Conteudo d={d} rotuloPeriodo={rotuloPeriodo} usuarioId={usuarioId} onGestor={(id) => setUsuarioId(usuarioId === id ? null : id)} />}
        </div>
    );
}

function Conteudo({ d, rotuloPeriodo, usuarioId, onGestor }: {
    d: DashGeral; rotuloPeriodo: string; usuarioId: number | null; onGestor: (id: number) => void;
}) {
    const leads = falhou(d.leads) ? null : d.leads;
    const gestores = falhou(d.gestores) ? null : d.gestores;
    const rec = leads?.receita;
    const base = leads?.base;
    const grupos = leads?.controle_grupos ?? {};
    const ativas = leads?.controle_paginas.cadastradas ?? 0;

    return (
        <>
            {/* ── Cards ─────────────────────────────────────────────────── */}
            {falhou(d.leads) ? <Indisponivel motivo={d.leads.erro} /> : (
                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,15rem),1fr))] gap-2.5">
                    <Kpi rotulo={`Receita total · ${rotuloPeriodo}`} valor={usd(rec?.total)} cor={COR.gold} destaque>
                        <span style={{ color: ok(rec?.variacao_pct) && rec!.variacao_pct! < 0 ? COR.red : COR.ok }}>{delta(rec?.variacao_pct)}</span> vs período anterior
                        <div className="mt-1 flex h-1 overflow-hidden rounded-sm" style={{ background: COR.broad }}>
                            <span style={{ width: `${rec?.flow_participacao ?? 0}%`, background: COR.flow }} />
                        </div>
                    </Kpi>
                    <Kpi rotulo={`Receita de flow · ${rotuloPeriodo}`} valor={usd(rec?.flow)} cor={COR.flow}>
                        {pct(rec?.flow_participacao, 0)} do total · automação das páginas
                    </Kpi>
                    <Kpi rotulo={`Receita de broadcast · ${rotuloPeriodo}`} valor={usd(rec?.broadcast)} cor={COR.broad}>
                        {pct(rec?.broadcast_participacao, 0)} do total · disparo em massa
                    </Kpi>
                    <Kpi rotulo="Páginas ativas" valor={int(ativas)} cor={COR.ink}>
                        {int(leads?.paises.length)} países · {int(leads?.controle_paginas.pausadas)} pausadas fora da conta
                    </Kpi>
                    <Kpi rotulo={`Leads captados · ${rotuloPeriodo}`} valor={int(base?.captados)} cor={COR.flow}>
                        {delta(base?.captados_variacao_pct)} vs anterior
                    </Kpi>
                    <Kpi rotulo="Leads perdidos · erro 551" valor={int(base?.perdidos_551)} cor={COR.red}>
                        {pct(base?.perdidos_sobre_captados_pct, 0)} do que entrou
                    </Kpi>
                    <Kpi rotulo={`Saldo da base · ${rotuloPeriodo}`} valor={assinado(base?.saldo)} cor={COR.ink}>
                        captados menos perdidos
                    </Kpi>
                    <Kpi rotulo={`Alcançados · ${rotuloPeriodo}`} valor={int(base?.alcancados_periodo)} cor={COR.blue}>
                        receberam mensagem · LTV {usd(base?.ltv_por_lead_alcancado)} por lead
                    </Kpi>
                </div>
            )}

            {/* ── Controle de páginas ───────────────────────────────────── */}
            {leads && (
                <Cartao titulo="Controle de páginas" sub={`${rotuloPeriodo} · quantas das ativas realmente trabalharam`}>
                    <div className="flex h-3 overflow-hidden rounded-sm bg-[#f0f2f5]">
                        {([['produtiva', COR.ok], ['captacao', COR.amber], ['dormente', COR.red]] as const).map(([k, cor]) => (
                            <span key={k} style={{ width: `${ativas ? ((grupos[k] ?? 0) / ativas) * 100 : 0}%`, background: cor }} />
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-3 text-[11.5px] text-[#5a6270]">
                        {([['produtiva', 'Produtivas', COR.ok], ['captacao', 'Só captação', COR.amber], ['dormente', 'Dormentes', COR.red]] as const).map(([k, nome, cor]) => (
                            <span key={k}>
                                <i className="mr-1.5 inline-block h-[9px] w-[9px] rounded-sm align-[-1px]" style={{ background: cor }} />
                                {nome} {int(grupos[k] ?? 0)} ({ativas ? Math.round(((grupos[k] ?? 0) / ativas) * 100) : 0}%)
                            </span>
                        ))}
                    </div>
                </Cartao>
            )}

            {/* ── Por gestor ─────────────────────────────────────────────── */}
            <Secao titulo="Por gestor" sub="só quem tem página ativa · clique numa linha para filtrar a tela (e as outras)" />
            {falhou(d.gestores) ? <Indisponivel motivo={d.gestores.erro} /> : gestores && gestores.length > 0 ? (
                <>
                    <ReceitaPorGestor gestores={gestores} rotuloPeriodo={rotuloPeriodo} onGestor={onGestor} />
                    <TabelaGestores gestores={gestores} usuarioId={usuarioId} onGestor={onGestor} />
                    <Secao titulo="Recado de cada gestor" sub="o que chama atenção em cada um" />
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),1fr))] gap-2.5">
                        {gestores.map((g) => <Recado key={g.user_id} g={g} selecionado={usuarioId === g.user_id} />)}
                    </div>
                </>
            ) : (
                <p className="text-[12.5px] text-[#5a6270]">Nenhum gestor com página ativa neste recorte.</p>
            )}

            {/* ── Envio ──────────────────────────────────────────────────── */}
            <Secao titulo="Envio" sub="flow contra broadcast" />
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] gap-2.5">
                <Cartao titulo="Cliques por dia" sub={rotuloPeriodo} direita={<Legenda itens={[[COR.flow, 'Flow'], [COR.broad, 'Broadcast']]} />}>
                    {falhou(d.envio.cliques_por_dia) ? <Indisponivel motivo={d.envio.cliques_por_dia.erro} /> : (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={d.envio.cliques_por_dia} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke={COR.grid} />
                                <XAxis dataKey="rot" tick={{ fontSize: 11, fill: COR.faint }} axisLine={{ stroke: COR.line }} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: COR.faint }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => NFC.format(v)} />
                                <Tooltip content={<Dica fmt={(v) => int(v)} />} cursor={{ fill: '#f4f6f8' }} />
                                <Bar dataKey="flow" name="Flow" fill={COR.flow} radius={[3, 3, 0, 0]} maxBarSize={32} />
                                <Bar dataKey="broadcast" name="Broadcast" fill={COR.broad} radius={[3, 3, 0, 0]} maxBarSize={32} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Cartao>
                <Cartao titulo="Dados totais · flow contra broadcast" sub={rotuloPeriodo}>
                    {falhou(d.envio.canais) || !d.envio.canais ? <Indisponivel motivo={falhou(d.envio.canais) ? d.envio.canais.erro : undefined} /> : (
                        <div className="overflow-auto">
                            <table className="w-full border-collapse text-[12.5px]">
                                <thead><tr>
                                    <th className={th}>Canal</th><th className={`${th} text-right`}>Enviadas</th><th className={`${th} text-right`}>Entrega</th>
                                    <th className={`${th} text-right`}>Cliques</th><th className={`${th} text-right`}>CTR</th>
                                    <th className={`${th} text-right`}>Receita</th><th className={`${th} text-right`}>RPC</th>
                                </tr></thead>
                                <tbody>
                                    {(['flow', 'broadcast'] as const).map((k) => {
                                        const c = (d.envio.canais as unknown as Record<'flow' | 'broadcast', CanalGeral>)[k];
                                        const bg = k === 'flow' ? COR.flowBg : COR.broadBg;
                                        return (
                                            <tr key={k} style={{ background: bg }}>
                                                <td className={`${td} font-medium text-[#14161a]`}>{k === 'flow' ? 'Flow' : 'Broadcast'}</td>
                                                <td className={tdN}>{int(c.enviadas)}</td>
                                                <td className={tdN}>{pct(c.taxa_entrega_pct)}</td>
                                                <td className={tdN}>{int(c.cliques)}</td>
                                                <td className={tdN}>{pct(c.ctr_pct, 2)}</td>
                                                <td className={`${tdN} font-semibold`} style={{ color: COR.gold }}>{usd(c.receita)}</td>
                                                <td className={tdN}>{ok(c.rpc) ? `US$ ${(c.rpc / 100).toFixed(4).replace('.', ',')}` : TRACO}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Cartao>
            </div>

            {/* ── Base de leads ──────────────────────────────────────────── */}
            <Secao titulo="Base de leads" sub={`entrada e perda por país · ${rotuloPeriodo}`} />
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] gap-2.5">
                <Cartao titulo="Entrada e perda por país" sub="direita é captação, esquerda é erro 551" direita={<Legenda itens={[[COR.flow, 'Captados'], [COR.red, 'Perdidos 551']]} />}>
                    {leads ? <EntradaPerdaPorPais paises={leads.paises} /> : <Indisponivel />}
                </Cartao>
                <Cartao titulo="Base que pode receber broadcast" sub={leads ? `Últimos ${leads.base_alcancavel.janela_dias} dias` : undefined}>
                    {leads?.base_alcancavel.ponte ? <BaseAlcancavel ponte={leads.base_alcancavel.ponte} serie={leads.base_alcancavel.serie ?? []} /> : <Indisponivel />}
                </Cartao>
            </div>

            {/* ── Rankings ───────────────────────────────────────────────── */}
            <Secao titulo="Rankings" sub={`${rotuloPeriodo} · receita por utm_content`} />
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] gap-2.5">
                <Cartao titulo="Top 10 funis" sub={rotuloPeriodo}>
                    {falhou(d.rankings.funis) ? <Indisponivel motivo={d.rankings.funis.erro} /> : (
                        <TabelaSimples
                            linhas={d.rankings.funis}
                            colunas={[
                                ['Funil', (f) => f.nome, true],
                                ['Cliques', (f) => int(f.cliques)],
                                ['CTR', (f) => pct(f.ctr_pct)],
                                ['Conclusão', (f) => pct(f.conclusao_pct)],
                                ['Receita', (f) => usd(f.receita), false, true],
                            ]}
                        />
                    )}
                </Cartao>
                <Cartao titulo="Top 10 broadcasts" sub={rotuloPeriodo}>
                    {falhou(d.rankings.broadcasts) ? <Indisponivel motivo={d.rankings.broadcasts.erro} /> : (
                        <TabelaSimples
                            linhas={d.rankings.broadcasts}
                            colunas={[
                                ['Grupo', (b) => b.nome || `#${b.id}`, true],
                                ['Hora', (b) => b.hora ?? TRACO],
                                ['Entrega', (b) => pct(b.taxa_entrega_pct)],
                                ['CTR', (b) => pct(b.ctr_pct, 2)],
                                ['Receita', (b) => usd(b.receita), false, true],
                            ]}
                        />
                    )}
                </Cartao>
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,24rem),1fr))] gap-2.5">
                <Cartao titulo="Receita por país" sub={rotuloPeriodo}>
                    {falhou(d.rankings.paises) ? <Indisponivel motivo={d.rankings.paises.erro} /> : <ReceitaPorPais linhas={d.rankings.paises} />}
                </Cartao>
                <Cartao titulo="Receita por domínio" sub={rotuloPeriodo}>
                    {falhou(d.rankings.dominios) ? <Indisponivel motivo={d.rankings.dominios.erro} /> : (
                        <TabelaSimples
                            linhas={[...d.rankings.dominios].sort((a, b) => (b.receita ?? 0) - (a.receita ?? 0))}
                            colunas={[
                                ['Domínio', (r) => r.nome, true],
                                ['Enviados', (r) => int(r.enviados)],
                                ['Cliques', (r) => int(r.cliques)],
                                ['Receita', (r) => usd(r.receita), false, true],
                            ]}
                        />
                    )}
                </Cartao>
            </div>
        </>
    );
}

// ── blocos ─────────────────────────────────────────────────────────────────
function ReceitaPorGestor({ gestores, rotuloPeriodo, onGestor }: {
    gestores: GestorGeral[]; rotuloPeriodo: string; onGestor: (id: number) => void;
}) {
    const soma = gestores.reduce((s, g) => s + g.receita, 0) || 1;
    const dados = gestores.map((g) => ({
        nome: nomeGestor(g), id: g.user_id, Flow: g.receita_flow, Broadcast: g.receita_broadcast,
        fatia: (g.receita / soma) * 100, total: g.receita,
        rotulo: `${usdK(g.receita)} · ${((g.receita / soma) * 100).toFixed(1).replace('.', ',')}%`,
    }));
    return (
        <Cartao
            titulo="Receita por gestor"
            sub={`${rotuloPeriodo} · barra dividida em flow e broadcast; a % é a fatia de cada gestor`}
            direita={<Legenda itens={[[COR.flow, 'Flow'], [COR.broad, 'Broadcast']]} />}
        >
            <ResponsiveContainer width="100%" height={Math.max(120, dados.length * 34 + 24)}>
                <BarChart data={dados} layout="vertical" margin={{ top: 0, right: 90, left: 8, bottom: 0 }}>
                    <CartesianGrid horizontal={false} stroke={COR.grid} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: COR.faint }} tickFormatter={(v) => usdK(v)} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="nome" width={130} tick={{ fontSize: 12, fill: COR.ink }} axisLine={false} tickLine={false} />
                    <Tooltip content={<Dica fmt={(v) => usd(v)} />} cursor={{ fill: '#f4f6f8' }} />
                    <Bar dataKey="Flow" stackId="r" fill={COR.flow} onClick={(_: unknown, i: number) => { if (dados[i]) onGestor(dados[i].id); }} cursor="pointer" />
                    <Bar
                        dataKey="Broadcast" stackId="r" fill={COR.broad} radius={[0, 3, 3, 0]} cursor="pointer"
                        onClick={(_: unknown, i: number) => { if (dados[i]) onGestor(dados[i].id); }}
                    >
                        <LabelList dataKey="rotulo" position="right" fontSize={11} fill={COR.gold} fontWeight={650} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Cartao>
    );
}

function TabelaGestores({ gestores, usuarioId, onGestor }: {
    gestores: GestorGeral[]; usuarioId: number | null; onGestor: (id: number) => void;
}) {
    return (
        <div className="max-h-[36rem] overflow-auto rounded-md border border-[#e3e6eb] bg-white">
            <table className="w-full border-collapse text-[12.5px]">
                <thead><tr>
                    <th className={th}>Gestor</th><th className={`${th} text-right`}>Páginas ativas</th>
                    <th className={`${th} text-right`}>Receita</th><th className={`${th} text-right`}>Δ período</th>
                    <th className={`${th} text-right`}>Captados</th><th className={`${th} text-right`}>Perdidos 551</th>
                    <th className={`${th} text-right`}>Saldo</th><th className={`${th} text-right`}>Funis ativos</th>
                    <th className={`${th} text-right`}>Broadcasts</th><th className={th}>Rotina</th>
                </tr></thead>
                <tbody>
                    {gestores.map((g) => (
                        <tr
                            key={g.user_id}
                            tabIndex={0}
                            onClick={() => onGestor(g.user_id)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onGestor(g.user_id); } }}
                            className={`cursor-pointer hover:bg-[#f8f9fb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2563eb] ${usuarioId === g.user_id ? 'bg-[#f1f5ff]' : ''}`}
                        >
                            <td className={`${td} font-medium text-[#14161a]`}>{nomeGestor(g)}</td>
                            <td className={tdN}>{int(g.paginas_ativas)}</td>
                            <td className={`${tdN} font-semibold`} style={{ color: COR.gold }}>{usd(g.receita)}</td>
                            <td className={tdN} style={{ color: ok(g.receita_variacao) ? (g.receita_variacao >= 0 ? COR.ok : COR.red) : undefined }}>{delta(g.receita_variacao)}</td>
                            <td className={tdN}>{int(g.captados)}</td>
                            <td className={tdN}>{int(g.perdidos_551)}</td>
                            <td className={tdN} style={{ color: g.saldo >= 0 ? COR.ok : COR.red }}>{assinado(g.saldo)}</td>
                            <td className={tdN}>{int(g.funis_ativos)}</td>
                            <td className={tdN}>{int(g.broadcasts)}</td>
                            <td className={td}>{g.rotinas_no_ar ? <Tag tom="ok">{g.rotinas_no_ar} no ar</Tag> : <Tag tom="crit">nenhuma</Tag>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** O que chama atenção num gestor, derivado só dos números dele. */
function avisosDoGestor(g: GestorGeral): string[] {
    const a: string[] = [];
    if (g.saldo < 0) a.push(`Perde mais leads do que capta: ${int(g.perdidos_551)} perdidos contra ${int(g.captados)} captados.`);
    if (!g.rotinas_no_ar) a.push('Nenhuma rotina no ar: os broadcasts dependem de criação manual.');
    if (g.dormentes > 0) a.push(`${int(g.dormentes)} página(s) ativa(s) sem lead, clique nem receita no período.`);
    if (g.funis_ativos === 0 && g.funis > 0) a.push(`Tem ${g.funis} funis e nenhum ativo.`);
    if (ok(g.receita_variacao) && g.receita_variacao <= -20) a.push(`Receita caiu ${Math.abs(g.receita_variacao).toFixed(0)}% contra o período anterior.`);
    if (!a.length) a.push('Sem alerta: capta mais do que perde, tem rotina no ar e as páginas ativas trabalharam.');
    return a;
}

function Recado({ g, selecionado }: { g: GestorGeral; selecionado: boolean }) {
    const acento = g.saldo < 0 ? COR.red : g.receita > 100000 ? COR.gold : COR.flow;
    return (
        <div
            className="flex flex-col gap-2 rounded-md border bg-white px-3.5 py-3"
            style={{ borderColor: selecionado ? COR.blue : COR.line, boxShadow: `inset 3px 0 0 ${acento}` }}
        >
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13.5px] font-semibold">{nomeGestor(g)}</span>
                <span className="text-base font-semibold tracking-[-0.3px]" style={{ color: g.receita ? COR.gold : COR.faint }}>{usd(g.receita)}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                <Tag tom="dark">{int(g.paginas_ativas)} páginas ativas</Tag>
                {g.rotinas_no_ar ? <Tag tom="ok">{g.rotinas_no_ar} rotina</Tag> : <Tag tom="crit">sem rotina</Tag>}
                <Tag tom={g.funis_ativos ? 'flow' : 'warn'}>{g.funis_ativos}/{g.funis} funis ativos</Tag>
                <Tag tom={g.broadcasts ? 'info' : 'warn'}>{int(g.broadcasts)} broadcasts</Tag>
            </div>
            <ul className="m-0 list-none space-y-1 p-0 text-[12.5px] leading-snug text-[#5a6270]">
                {avisosDoGestor(g).map((t) => <li key={t}>{t}</li>)}
            </ul>
        </div>
    );
}

function EntradaPerdaPorPais({ paises }: { paises: { codigo: string; captados: number; perdidos_551: number; saldo: number }[] }) {
    const dados = [...paises]
        .sort((a, b) => (b.captados + b.perdidos_551) - (a.captados + a.perdidos_551))
        .slice(0, 15)
        .map((p) => ({ pais: regiao(p.codigo), Captados: p.captados, 'Perdidos 551': -p.perdidos_551 }));
    if (!dados.length) return <p className="text-[12.5px] text-[#5a6270]">Sem leads no período.</p>;
    return (
        <ResponsiveContainer width="100%" height={dados.length * 22 + 30}>
            <BarChart data={dados} layout="vertical" stackOffset="sign" margin={{ top: 0, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid horizontal={false} stroke={COR.grid} />
                <XAxis type="number" tick={{ fontSize: 11, fill: COR.faint }} tickFormatter={(v) => NFC.format(Math.abs(v))} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="pais" width={110} tick={{ fontSize: 12, fill: COR.ink }} axisLine={false} tickLine={false} />
                <Tooltip content={<Dica fmt={(v) => int(Math.abs(v))} />} cursor={{ fill: '#f4f6f8' }} />
                <Bar dataKey="Captados" stackId="s" fill={COR.flow} />
                <Bar dataKey="Perdidos 551" stackId="s" fill={COR.red} />
            </BarChart>
        </ResponsiveContainer>
    );
}

/** A base que pode receber broadcast, dia a dia: a linha mostra a tendência; a
 *  ponte (entraram, perdidos, outros) fica numa linha de resumo embaixo. */
function BaseAlcancavel({ ponte, serie }: {
    ponte: { inicio: number; entraram: number; perdidos_551: number; outros: number; fim: number };
    serie: { data: string; alcancaveis: number; captados: number; perdidos_551: number }[];
}) {
    const variacao = ponte.inicio ? ((ponte.fim - ponte.inicio) / ponte.inicio) * 100 : null;
    const cresceu = ponte.fim >= ponte.inicio;
    const cor = cresceu ? COR.ok : COR.red;
    const dados = serie.map((p) => ({ ...p, dia: p.data.slice(8, 10) + '/' + p.data.slice(5, 7) }));

    function DicaBase({ active, payload }: { active?: boolean; payload?: { payload: typeof dados[number] }[] }) {
        if (!active || !payload?.length) return null;
        const p = payload[0].payload;
        return (
            <div className="rounded-md border border-[#e3e6eb] bg-white px-2.5 py-1.5 text-[11.5px] shadow-md">
                <div className="mb-0.5 font-mono text-[10px] uppercase tracking-wider text-[#8c93a0]">{p.dia}</div>
                <div><b>{int(p.alcancaveis)}</b> podem receber</div>
                <div style={{ color: COR.ok }}>+{int(p.captados)} entraram</div>
                <div style={{ color: COR.red }}>−{int(p.perdidos_551)} perdidos 551</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-[27px] font-semibold leading-none tracking-[-0.8px] tabular-nums">{int(ponte.fim)}</span>
                <span className="text-[12.5px] font-medium" style={{ color: cor }}>
                    {ok(variacao) ? `${cresceu ? '▲' : '▼'} ${Math.abs(variacao).toFixed(1).replace('.', ',')}%` : TRACO}
                </span>
                <span className="text-[12.5px] text-[#5a6270]">podem receber broadcast hoje</span>
            </div>
            {dados.length > 1 ? (
                <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={dados} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="base-alc" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={cor} stopOpacity={0.22} />
                                <stop offset="100%" stopColor={cor} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke={COR.grid} />
                        <XAxis dataKey="dia" tick={{ fontSize: 11, fill: COR.faint }} axisLine={{ stroke: COR.line }} tickLine={false} interval="preserveStartEnd" minTickGap={24} />
                        <YAxis tick={{ fontSize: 11, fill: COR.faint }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => NFC.format(v)} domain={['auto', 'auto']} />
                        <Tooltip content={<DicaBase />} />
                        <Area type="monotone" dataKey="alcancaveis" stroke={cor} strokeWidth={2} fill="url(#base-alc)" dot={false} activeDot={{ r: 3 }} />
                    </AreaChart>
                </ResponsiveContainer>
            ) : null}
            <p className="m-0 text-[12px] text-[#5a6270]">
                Na janela: <b style={{ color: COR.ok }}>+{int(ponte.entraram)}</b> entraram ·{' '}
                <b style={{ color: COR.red }}>−{int(ponte.perdidos_551)}</b> perdidos 551 ·{' '}
                <span title="O que fecha a conta e não é captação nem 551: lead desativado por outro erro, bloqueado, página pausada ou reativada no meio da janela.">
                    <b>{assinado(ponte.outros)}</b> outras saídas e entradas ⓘ
                </span>
            </p>
        </div>
    );
}

function ReceitaPorPais({ linhas }: { linhas: { nome: string; receita?: number | null }[] }) {
    const dados = [...linhas]
        .filter((l) => (l.receita ?? 0) > 0)
        .sort((a, b) => (b.receita ?? 0) - (a.receita ?? 0))
        .slice(0, 12)
        .map((l) => ({ pais: l.nome, Receita: l.receita ?? 0, rotulo: usdK(l.receita) }));
    if (!dados.length) return <p className="text-[12.5px] text-[#5a6270]">Sem receita atribuída no período.</p>;
    return (
        <ResponsiveContainer width="100%" height={dados.length * 22 + 10}>
            <BarChart data={dados} layout="vertical" margin={{ top: 0, right: 70, left: 8, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="pais" width={110} tick={{ fontSize: 12, fill: COR.ink }} axisLine={false} tickLine={false} />
                <Tooltip content={<Dica fmt={(v) => usd(v)} />} cursor={{ fill: '#f4f6f8' }} />
                <Bar dataKey="Receita" radius={[0, 3, 3, 0]}>
                    {dados.map((r, i) => <Cell key={r.pais} fill={i < 3 ? COR.gold : '#e2bd78'} />)}
                    <LabelList dataKey="rotulo" position="right" fontSize={11} fill={COR.gold} fontWeight={650} />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

function TabelaSimples<T>({ linhas, colunas }: {
    linhas: T[];
    /** [título, valor, é a coluna-chave, é dinheiro] */
    colunas: [string, (l: T) => string, boolean?, boolean?][];
}) {
    if (!linhas.length) return <p className="text-[12.5px] text-[#5a6270]">Nada no período.</p>;
    return (
        <div className="max-h-[36rem] overflow-auto">
            <table className="w-full border-collapse text-[12.5px]">
                <thead><tr>{colunas.map(([t, , chave]) => <th key={t} className={`${th} ${chave ? '' : 'text-right'}`}>{t}</th>)}</tr></thead>
                <tbody>
                    {linhas.map((l, i) => (
                        <tr key={i}>
                            {colunas.map(([t, f, chave, dinheiro]) => (
                                <td
                                    key={t}
                                    className={chave ? `${td} font-medium text-[#14161a]` : `${tdN} ${dinheiro ? 'font-semibold' : ''}`}
                                    style={dinheiro ? { color: COR.gold } : undefined}
                                >
                                    {f(l)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
